import { PersonDto } from '../generated/api-client';
import { Member } from '../../../features/units/models/unit.model';
import { Person, PersonAssignment, PersonMinistry, PersonMinistryActivity, PersonMinistryAppointment } from '../../../features/people/models/person.model';

/**
 * Parsea una fecha en formato YYYY-MM-DD a un objeto Date local,
 * evitando el problema de timezone que ocurre con new Date('YYYY-MM-DD')
 * (que interpreta la cadena como UTC midnight).
 */
function parseDateLocal(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Convierte un PersonDto generado por NSwag al tipo de dominio Member.
 * Usado por features que muestran listas de miembros de una unidad.
 */
export function mapPersonToMember(dto: PersonDto): Member {
  return {
    id: dto.id ?? '',
    photo: dto.photoUrl ?? undefined,
    firstName: dto.firstName ?? '',
    lastName: dto.lastName ?? '',
  };
}

/**
 * Convierte un PersonDto generado por NSwag al tipo de dominio Person.
 *
 * Los enums del backend (SpiritualStage, MembershipStatus, etc.) se convierten
 * a sus valores de string para que los componentes no dependan de los enums generados.
 */
export function mapPersonDtoToPerson(dto: PersonDto): Person {
  return {
    id: dto.id ?? '',
    firstName: dto.firstName ?? '',
    lastName: dto.lastName ?? '',
    email: dto.email ?? undefined,
    documentType: dto.documentType ?? undefined,
    documentNumber: dto.documentNumber ?? undefined,
    dateOfBirth: dto.birthDate ? parseDateLocal(dto.birthDate) : undefined,
    gender: dto.gender ?? undefined,
    relationshipSituation: (dto as PersonDto & { relationshipSituation?: string }).relationshipSituation ?? undefined,
    relationshipPersonId: (dto as PersonDto & { relationshipPersonId?: string }).relationshipPersonId ?? undefined,
    relationshipPersonName: (dto as PersonDto & { relationshipPersonName?: string }).relationshipPersonName ?? undefined,
    occupation: dto.occupation ?? undefined,
    previousReligion: dto.previousReligion ?? undefined,
    isHomeAttended: (dto as PersonDto & { isHomeAttended?: boolean }).isHomeAttended === true,
    placeOfBirth: dto.placeOfBirth ?? undefined,
    countryOfBirthId: dto.countryOfBirthId ?? undefined,
    countryOfBirthName: dto.countryOfBirthName ?? undefined,
    nationalityId: dto.nationalityId ?? undefined,
    nationalityName: dto.nationalityName ?? undefined,
    languages: dto.languages ?? undefined,
    ageStatusOverride: dto.ageStatusOverride ?? undefined,
    address: dto.address ?? undefined,
    phone: dto.phone ?? undefined,
    communityId: dto.membershipUnitId ?? '',
    communityName: dto.membershipUnitName ?? 'Sin comunidad',
    responsiblePriestId: (dto as PersonDto & { responsiblePriestId?: string }).responsiblePriestId ?? undefined,
    responsiblePriestName: (dto as PersonDto & { responsiblePriestName?: string }).responsiblePriestName ?? undefined,
    ministry: dto.ministry != null ? ({
      ministryId: dto.ministry.ministryId ?? 0,
      ministryName: dto.ministry.ministryName ?? '',
      status: dto.ministry.status ?? '',
      unitId: dto.ministry.unitId ?? '',
      unitName: dto.ministry.unitName ?? '',
      activities: (dto.ministry.activities ?? [])
        .filter(activity => activity.validTo == null)
        .map(activity => ({
          id: activity.id ?? 0,
          unitId: activity.unitId ?? '',
          unitName: activity.unitName ?? '',
          validFrom: activity.validFrom ?? '',
          validTo: activity.validTo ?? undefined,
        } as PersonMinistryActivity)),
    } as PersonMinistry) : undefined,
    appointments: (dto.ministryAppointments ?? []).map(appointment => ({
      ministryAppointmentName: appointment.ministryAppointmentName ?? '',
      unitId: appointment.unitId ?? '',
      unitName: appointment.unitName ?? '',
    } as PersonMinistryAppointment)),
    assignments: (dto.assignments ?? []).map(assignment => ({
      assignmentName: assignment.assignmentName ?? '',
      unitId: assignment.unitId ?? '',
      unitName: assignment.unitName ?? '',
    } as PersonAssignment)),
    photo: dto.photoUrl ?? undefined,
    spiritualStage: dto.spiritualStage ?? undefined,
    status: dto.status ?? undefined,
    ageStatus: dto.ageStatus ?? undefined,
    hasActiveUser: dto.hasActiveUser === true,
    hasLinkedUser: dto.hasLinkedUser === true,
    userId: dto.userId ?? undefined,
  };
}
